import { useState, useEffect } from "react";
import { Card, Input, Table } from "antd";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import api from "../interceptors/api";
import { useTranslation } from "react-i18next";
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

export default function ClipboardToLink() {
  const { t } = useTranslation();
  const [clipboardData, setClipboardData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const loadFiles = async () => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return;

      setListLoading(true);
      const res = await api.get("/clipboard-to-link/list");
      setFiles(res.data.files || []);
    } catch (e) {
      toast.error("Failed to load clipboard history");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handlePaste = async (event) => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) {
      toast.error('Please log in to use this feature');
      return;
    }

    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const items = clipboard.items;
    let file = null;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        file = item.getAsFile();
        await uploadClipboard(file);
        return;
      }
    }

    const text = clipboard.getData("text");
    if (text) {
      file = new File([new Blob([text], { type: "text/plain" })], "clipboard.txt", {
        type: "text/plain",
      });
      await uploadClipboard(file);
    }
  };

  const uploadClipboard = async (file) => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) return;

    setUploading(true);

    const toastId = file.type === "text/plain" ? toast.loading(t("Text") + t("uploading")) : toast.loading(t("Image") + t("uploading"));

    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post("/clipboard-to-link/upload", formData);

      toast.success(t("upload_success"), { id: toastId });
      await loadFiles();
    } catch (err) {
      toast.error(t("upload_error"), { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("copied_to_clipboard") || "Link copied to clipboard!");
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t("copied_to_clipboard") || "Link copied to clipboard!");
    }
  };

  const columns = [
    { title: t("filename"), dataIndex: "filename", key: "filename" },
    { 
      title: t("size_label"), 
      dataIndex: "size_bytes", 
      key: "size_bytes", 
      width: 140,
      render: (size) => {
        if (!size) return '-';
        const sizeMB = (size / (1024 * 1024)).toFixed(2);
        return `${sizeMB} MB`;
      }
    },
    { title: t("type_label"), dataIndex: "mime_type", key: "mime_type", width: 220 },
    {
      title: t("actions") || "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div className="flex space-x-2">
          <StandardButton
            type="secondary"
            size="small"
            href={record.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center"
          >
            {t("open")}
          </StandardButton>
          <StandardButton
            type="secondary"
            size="small"
            onClick={() => copyToClipboard(record.url)}
            className="flex items-center"
          >
            {t("copy")}
          </StandardButton>
        </div>
      ),
    },
  ];

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto" onPaste={handlePaste}>
        <Card className="p-6 rounded-xl shadow-lg space-y-5">
          <div className="text-2xl font-extrabold text-center text-gray-800 py-4 bg-yellow-100 border border-yellow-300 rounded-lg">
            {t("paste_here_to_upload")}
          </div>
          <div className="mt-8">
            <div className="text-sm font-semibold mb-3">{t("all_files")}</div>
            <Table
              rowKey="id"
              dataSource={files}
              columns={columns}
              loading={listLoading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
}